import {inject, Injectable} from '@angular/core';
import {MemberStore} from '../../stores/member.store';
import {VaultsService} from '../../api/services/vaults.service';
import {VaultMembersService} from '../../api/services/vault-members.service';
import {DialogService} from '../dialog/dialog.service';
import {TranslationKey} from '../../types/available-translations.type';
import {HttpErrorResponse} from '@angular/common/http';
import {firstValueFrom} from 'rxjs';
import {AuthService} from '../auth/auth.service';

@Injectable({
    providedIn: 'root'
})
export class MemberService {
    private readonly memberStore = inject(MemberStore);
    private readonly apiVaultService = inject(VaultsService);
    private readonly apiVaultMemberService = inject(VaultMembersService);
    private readonly authService = inject(AuthService);
    private readonly dialogService = inject(DialogService);

    public async fetchMembers(vaultId: string): Promise<boolean> {
        this.memberStore.setLoading(true);
        let status = false;
        const bearer = await this.authService.getBearer();
        try {
            const vaultMembers = await firstValueFrom(this.apiVaultService.VaultsGetMembers({
                Authorization: bearer,
                vaultId
            }));
            this.memberStore.set(vaultMembers);
            status = true;
        } catch (error) {
            console.error(error);
        } finally {
            this.memberStore.setLoading(false);
        }
        return status;
    }

    public async removeMemberFromVault(memberId: string): Promise<Record<string, boolean>> {
        this.memberStore.setLoading(true);
        const status: Record<string, boolean> = {};
        status[memberId] = false;

        const bearer = await this.authService.getBearer();
        try {
            await firstValueFrom(this.apiVaultMemberService.VaultMembersRemoveVaultMember({
                Authorization: bearer,
                memberId
            }));
            this.memberStore.remove(memberId);
            status[memberId] = true;
        } catch (error) {
            const httpError = error as HttpErrorResponse;
            console.error(httpError);
            // dialog requirement: https://jira.erminas.de/browse/APT-374 (msgbox)
            if (httpError.error.code !== undefined) {
                this.dialogService.showDialog({
                    messageTranslationKey: 'ERROR.CODES.CODE_' + httpError.error.code as TranslationKey
                }, 'unknown-error-failed-to-remove-user-from-vault');
            } else {
                this.dialogService.showDialog({
                    messageTranslationKey: 'ERROR.REMOVE_MEMBER_FROM_VAULT'
                }, 'failed-to-remove-user-from-vault');
            }
        } finally {
            this.memberStore.setLoading(false);
        }
        return status;
    }
}
